import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    OnDestroy,
} from '@angular/core';
import * as fromAlgorithmForm from '../../../algorithm/models/algorithm-form';
import {
    AlgorithmDTO,
    AlgorithmParameter,
    AlgorithmType,
} from '../../models/algorithm-form';
import { FormControl, FormGroup } from '@angular/forms';
import { AlgorithmService } from '../../services/algorithm.service';
import { AlgorithmFormService } from '../../services/algorithm-form.service';
import { TrainingType } from '../../../shared/enums/training-type.enum';
import { nonNil } from '../../../shared/utility';
import { BeforeTrainingStart } from '../../../shared/interfaces/before-training-start';
import { Observable, of, Subscription } from 'rxjs';
import { Models } from '../../../skill-architecture/models/skill-architecture-form';
import { SelectedModelService } from '../../../shared/services/selected-model.service';

@Component({
    selector: 'app-algorithm',
    templateUrl: './algorithm.component.html',
    styleUrls: ['./algorithm.component.scss'],
    standalone: false,
})
export class AlgorithmComponent
    extends BeforeTrainingStart
    implements OnInit, OnChanges, OnDestroy
{
    // html import
    protected readonly AlgorithmType = AlgorithmType;
    protected readonly TrainingType = TrainingType;

    selectedModel: Models = null;
    private subscription: Subscription;

    @Input() trainingId: string;
    @Input() trainingType: TrainingType;
    @Input() editingIsDisabled: boolean = false;

    @ViewChild('beforeStartComponent') currentChild: BeforeTrainingStart;

    dataLoaded: boolean = false;
    advancedSetting: boolean = false;
    form: FormGroup;

    algorithmTypes: fromAlgorithmForm.AlgorithmType[];

    type: FormControl<fromAlgorithmForm.AlgorithmType>;

    constructor(
        private algorithmService: AlgorithmService,
        private formService: AlgorithmFormService,
        private selectedModelService: SelectedModelService
    ) {
        super();
    }

    ngOnInit(): void {
        this.algorithmService
            .getAlgorithmFormData(this.trainingId)
            .subscribe((algorithm: AlgorithmDTO) => {
                this.initializeTypeForm(algorithm.type);
                this.initializeAlgorithmForm(algorithm);

                this.advancedSetting = algorithm.advancedSetting;
                this.dataLoaded = true;
            });
        this.subscription = this.selectedModelService
            .getMessage()
            .subscribe((model) => {
                this.setAlgorithmTypes(this.trainingType, model);
            });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    saveBeforeStart(): Observable<any> {
        if (this.currentChild) {
            return this.currentChild.saveBeforeStart();
        }
        return of(null);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.editingIsDisabled &&
            !changes.editingIsDisabled.isFirstChange()
        ) {
            if (this.editingIsDisabled) {
                this.type.disable();
            } else {
                this.type.enable();
            }
        }
    }

    initializeTypeForm(type: AlgorithmType) {
        this.type = new FormControl<AlgorithmType>(type);
        this.updateAlgorithmFormState();
    }

    updateAlgorithmFormState() {
        // algorithmTypes is populated asynchronously via the selectedModelService
        // subscription, which may not have emitted yet (e.g. right after
        // initializeTypeForm runs on the algorithm-data response).
        if (this.type && this.algorithmTypes) {
            if (this.editingIsDisabled || this.algorithmTypes.length < 2) {
                this.type.disable();
            } else if (
                !this.editingIsDisabled &&
                this.algorithmTypes.length >= 2
            ) {
                this.type.enable();
            }
        }
    }

    initializeAlgorithmForm(data: AlgorithmDTO) {
        this.form = this.formService.getForm(data);
    }

    setAlgorithmTypes(trainingType: TrainingType, model: Models | undefined) {
        switch (trainingType) {
            case TrainingType.MOTORIC:
                this.algorithmTypes = [
                    AlgorithmType.PPO,
                    AlgorithmType.SAC,
                    AlgorithmType.BC,
                    AlgorithmType.GAIL,
                    AlgorithmType.AIRL,
                ];
                break;
            case TrainingType.VISUAL:
                this.algorithmTypes =
                    model === Models.grounding_dino
                        ? [AlgorithmType.ADAMW]
                        : [AlgorithmType.ADAM];
                break;
            default:
                this.algorithmTypes = [];
        }
        this.updateAlgorithmFormState();
        if (this.type && !this.algorithmTypes.includes(this.type.value)) {
            this.type.setValue(this.algorithmTypes[0]);
            this.onTypeValueChange();
        }
    }

    slideToggleChange($event) {
        if (!this.editingIsDisabled) {
            this.advancedSetting = $event.checked;
            this.onValueChange();
        }
    }

    onValueChange($event?) {
        if (!this.editingIsDisabled) {
            const algorithmDTO: AlgorithmDTO = this.prepareAlgorithmDto();

            this.adjustValuesForSaving(algorithmDTO.parameters);

            this.algorithmService
                .saveAlgorithmFormData(this.trainingId, algorithmDTO)
                .subscribe((algorithmDto: AlgorithmDTO) => {
                    if ($event === 'changedLearner') {
                        this.initializeAlgorithmForm(algorithmDto);
                        this.dataLoaded = true;
                    }
                });
        }
    }

    onTypeValueChange() {
        if (!this.editingIsDisabled) {
            this.dataLoaded = false;
            this.advancedSetting = false;

            // SaveAlgorithm will create a new Algorithm if send with empty parameters (duh, obviously!)
            const algorithmDTO: AlgorithmDTO = this.prepareAlgorithmDto(null);
            this.algorithmService
                .saveAlgorithmFormData(this.trainingId, algorithmDTO)
                .subscribe((data: AlgorithmDTO) => {
                    this.initializeAlgorithmForm(data);
                    this.dataLoaded = true;
                });
        }
    }

    private prepareAlgorithmDto(
        rawFormValues: Object = this.form.getRawValue()
    ): AlgorithmDTO {
        let parameters = [];
        if (nonNil(rawFormValues)) {
            Object.keys(rawFormValues).forEach((scope) => {
                Object.entries(rawFormValues[scope]).forEach(
                    ([name, value]) => {
                        parameters.push({
                            name,
                            value,
                            scope,
                        } as AlgorithmParameter);
                    }
                );
            });
        }
        return {
            type: this.type.value,
            parameters: parameters,
            advancedSetting: this.advancedSetting,
        };
    }

    private adjustValuesForSaving(parameters: AlgorithmParameter[]) {
        parameters.forEach((parameter: AlgorithmParameter) => {
            const name = parameter.name;
            if (name === 'epsilon') {
                parameter.value = Math.pow(10, parameter.value as number);
            }

            let value = parameter.value;

            if (typeof value === 'string') {
                if (value.includes('.') || value.includes(',')) {
                    parameter.value = parseFloat(
                        parameter.value.replace(',', '.')
                    );
                } else {
                    if (parameter.value === '') {
                        parameter.value = null;
                    } else {
                        const numeric = parseInt(parameter.value);
                        if (!isNaN(numeric)) {
                            parameter.value = numeric;
                        }
                    }
                }
            }
        });
    }
}
